import { Injectable, inject, signal, computed } from '@angular/core';
import { MovementsService } from '../services/movements.service';
import { MovementHistoryFilters, SortOrder } from '../models/movements';
import { Movement } from './types';

@Injectable({ providedIn: 'root' })
export class MovementsStore {
  private movementsService = inject(MovementsService);

  // State signals
  currentPage = signal(1);
  currentLimit = signal(50);
  loadingMovements = signal(false);
  loadingMore = signal(false);
  movements = signal<Movement[]>([]);
  currentAccountNumber = signal<string>('');
  totalElements = signal(0);
  hasNextPage = signal(false);

  // Error handling
  error = signal<string | null>(null);

  // Current filters
  currentFilters = signal<MovementHistoryFilters>({});

  // Computed values
  hasMovements = computed(() => this.movements().length > 0);
  isFirstPage = computed(() => this.currentPage() === 1);
  canLoadMore = computed(() => this.hasNextPage() && !this.loadingMovements());

	clearFilters() {
		this.currentFilters.set({})
	}
  /**
   * Load movements for a specific account
   * @param showSkeleton - Si es true, muestra el skeleton loader. Si es false, recarga en segundo plano.
   */
  async loadMovements(
    accountNumber: string,
    filters: MovementHistoryFilters = {},
    loadMore = false,
    showSkeleton = true
  ) {
    if (this.loadingMovements()) return;

    // Only set loadingMovements for initial loads, not for loadMore, and only if showSkeleton is true
    if (!loadMore && showSkeleton) {
      this.loadingMovements.set(true);
    }
    this.error.set(null);

    try {
      // Set account number if different
      if (accountNumber !== this.currentAccountNumber()) {
        this.currentAccountNumber.set(accountNumber);
        this.clearMovements();
        loadMore = false;
      }

      // Prepare filters with pagination
      const pageToRequest = loadMore ? this.currentPage() : 1;
      const requestFilters: MovementHistoryFilters = {
				...filters,
        pagination: {
          page: pageToRequest,
          limit: this.currentLimit(),
          sortBy: filters.pagination?.sortBy || 'id',
          sortOrder: filters.pagination?.sortOrder || SortOrder.DESC
        }
      };

      const response = await this.movementsService.getMovementHistory(
        accountNumber,
        requestFilters
      );

      if (response.error) {
        this.error.set(response.error?.message || 'Error loading movements');
        return;
      }

      const movementsData = response.data;

      if (movementsData?.elements?.length) {
        if (loadMore && this.movements().length > 0) {
          // Append new movements to existing ones
          this.movements.update((existing) => [...existing, ...movementsData.elements]);
        } else {
          // Replace all movements
          this.movements.set(movementsData.elements);
          this.currentPage.set(1);
        }

        // Update pagination info
        this.totalElements.set(movementsData.pagination?.totalElements || 0);
        this.hasNextPage.set(movementsData.pagination?.hasNextPage || false);
        
        // Only update currentPage if it's not a loadMore operation
        if (!loadMore) {
          this.currentPage.set(movementsData.pagination?.currentPage || 1);
        }

        // Store current filters
        this.currentFilters.set(requestFilters);
      } else {
        if (!loadMore) {
          this.movements.set([]);
        }
        this.hasNextPage.set(false);
      }
    } catch (error) {
      console.error('Error loading movements:', error);
      this.error.set('Failed to load movements');
    } finally {
      this.loadingMovements.set(false);
      this.loadingMore.set(false);
    }
  }

  /**
   * Reload movements from the beginning
   * @param showSkeleton - Si es true, muestra el skeleton loader. Si es false, recarga en segundo plano.
   */
  async reloadMovements(showSkeleton = true) {
    if (!this.currentAccountNumber()) return;

    this.currentPage.set(1);
    this.movements.set([]);
    await this.loadMovements(this.currentAccountNumber(), this.currentFilters(), false, showSkeleton);
  }

  /**
   * Load more movements (pagination)
   */
  async loadMoreMovements() {
    if (!this.canLoadMore() || !this.currentAccountNumber()) return;

    // Set loading state before making the request
    this.loadingMore.set(true);
    
    try {
      // Increment page number
      const nextPage = this.currentPage() + 1;
      this.currentPage.set(nextPage);
      
      // Load movements with loadMore flag
      await this.loadMovements(this.currentAccountNumber(), this.currentFilters(), true);
      
    } catch (error) {
      // Reset page on error
      this.currentPage.update((page) => page - 1);
      throw error;
    }
  }

  /**
   * Clear all movements data
   */
  clearMovements() {
    this.movements.set([]);
    this.currentPage.set(1);
    this.totalElements.set(0);
    this.hasNextPage.set(false);
    this.error.set(null);
    this.currentFilters.set({});
  }

  /**
   * Update pagination settings
   */
  updatePagination(page?: number, limit?: number) {
    if (page !== undefined) {
      this.currentPage.set(page);
    }
    if (limit !== undefined) {
      this.currentLimit.set(limit);
    }
  }

  clearStore() {
    this.currentPage.set(1);
    this.movements.set([]);
    this.totalElements.set(0);
    this.hasNextPage.set(false);
    this.error.set(null);
    this.currentFilters.set({});
  }
}
